import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listDidGroups,
  getDidGroup,
  createDidGroup,
  updateDidGroup,
  deleteDidGroup,
  releaseDidGroup,
  removeQuarantinedFromGroup,
  getReleaseJob,
  addGroupMembers,
  removeGroupMember,
  listDids,
  type DidGroup,
  type Did,
  type ReleaseJob,
} from "../api/dids";

function statusBadge(d: Did) {
  if (d.is_retired)
    return <span className="px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-700">retired</span>;
  if (d.is_blocked || d.is_quarantined)
    return (
      <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800" title={d.block_reason ?? ""}>
        {d.block_reason ? `blocked: ${d.block_reason}` : "quarantined"}
      </span>
    );
  return <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">active</span>;
}

function AddMembersModal({
  groupId,
  onClose,
  onSaved,
}: {
  groupId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Only DIDs not already in a group can be added (one-group-per-DID rule).
  const { data, isLoading } = useQuery({
    queryKey: ["dids", "unassigned"],
    queryFn: () => listDids({ unassigned: true, per_page: 200 }),
  });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const save = useMutation({
    mutationFn: () => addGroupMembers(groupId, [...selected]),
    onSuccess: onSaved,
  });

  const dids = data?.data ?? [];
  const toggle = (id: number) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-20 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-full overflow-auto space-y-3">
        <h2 className="text-lg font-bold">Add numbers to group</h2>
        {isLoading ? (
          <p className="text-gray-500 text-sm">Loading…</p>
        ) : dids.length === 0 ? (
          <p className="text-gray-500 text-sm">No unassigned numbers available. Every number is already in a group.</p>
        ) : (
          <div className="border border-gray-200 rounded max-h-80 overflow-auto divide-y divide-gray-100">
            {dids.map((d) => (
              <label key={d.did_id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
                <input type="checkbox" checked={selected.has(d.did_id)} onChange={() => toggle(d.did_id)} />
                <span className="font-mono">{d.phone}</span>
                <span className="text-gray-400">{d.name || ""}</span>
              </label>
            ))}
          </div>
        )}
        {save.isError && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
        <div className="flex gap-2 pt-2">
          <button
            disabled={selected.size === 0 || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Adding…" : `Add ${selected.size || ""}`}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function GroupDetail({ group }: { group: DidGroup }) {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["did-group", group.did_group_id],
    queryFn: () => getDidGroup(group.did_group_id),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["did-group", group.did_group_id] });
    qc.invalidateQueries({ queryKey: ["did-groups"] });
    qc.invalidateQueries({ queryKey: ["dids"] });
  };
  const remove = useMutation({
    mutationFn: (didId: number) => removeGroupMember(group.did_group_id, didId),
    onSuccess: invalidate,
  });
  // Track the active background release job so we can poll + show a progress bar.
  const [jobId, setJobId] = useState<number | null>(null);
  const { data: job } = useQuery({
    queryKey: ["release-job", jobId],
    queryFn: () => getReleaseJob(jobId as number),
    enabled: jobId != null,
    // Poll while running; stop once the worker reports it finished.
    refetchInterval: (q) => {
      const s = (q.state.data as ReleaseJob | undefined)?.status;
      return s === "complete" || s === "failed" ? false : 1500;
    },
  });
  // When the job finishes, refresh the member list so released numbers drop off.
  const jobDone = job?.status === "complete" || job?.status === "failed";
  const [lastSeenDone, setLastSeenDone] = useState<number | null>(null);
  if (jobDone && job && lastSeenDone !== job.job_id) {
    setLastSeenDone(job.job_id);
    invalidate();
  }

  const release = useMutation({
    mutationFn: () => releaseDidGroup(group.did_group_id),
    onSuccess: (r) => {
      invalidate();
      if (r.job_id) setJobId(r.job_id);
      else alert(r.message || "Nothing to release.");
    },
    onError: (e: unknown) => alert(`Release failed: ${(e as Error).message}`),
  });
  const removeQ = useMutation({
    mutationFn: () => removeQuarantinedFromGroup(group.did_group_id),
    onSuccess: (r) => {
      invalidate();
      if (r.job_id) setJobId(r.job_id);
      else alert(r.message || "No quarantined numbers to release.");
    },
    onError: (e: unknown) => alert(`Failed: ${(e as Error).message}`),
  });
  const jobRunning = jobId != null && !jobDone;
  function onRemoveQuarantined() {
    const typed = window.prompt(
      `⚠️ RELEASE QUARANTINED in "${group.name}" — this DISCONNECTS every quarantined ` +
      `number in this group at the carrier (Bandwidth) and permanently removes them from ` +
      `your account. This CANNOT be undone.\n\nType RELEASE to confirm:`);
    if (typed === "RELEASE") removeQ.mutate();
  }

  const members = data?.members ?? [];

  function onRelease() {
    const n = members.length;
    if (!n) { alert("This group has no numbers to release."); return; }
    const typed = window.prompt(
      `⚠️ RELEASE "${group.name}" — this DISCONNECTS all ${n} number(s) at the carrier ` +
      `(Bandwidth) and permanently removes them from your account. This CANNOT be undone — ` +
      `the numbers are gone.\n\nType RELEASE to confirm:`);
    if (typed === "RELEASE") release.mutate();
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <h2 className="font-semibold">
          {group.name} <span className="text-gray-400 text-sm">— {members.length} number(s)</span>
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={onRemoveQuarantined}
            disabled={removeQ.isPending || jobRunning}
            title="Disconnect every quarantined number in this group at the carrier and remove them permanently"
            className="border border-red-300 text-red-700 hover:bg-red-50 rounded px-3 py-1.5 text-sm disabled:opacity-50"
          >
            {removeQ.isPending ? "Starting…" : "Release quarantined"}
          </button>
          <button
            onClick={onRelease}
            disabled={release.isPending || jobRunning}
            title="Disconnect every number in this group at the carrier and remove them permanently"
            className="border border-red-300 text-red-700 hover:bg-red-50 rounded px-3 py-1.5 text-sm disabled:opacity-50"
          >
            {release.isPending ? "Starting…" : "Release group"}
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="bg-brand hover:bg-brand-dark text-white rounded px-3 py-1.5 text-sm"
          >
            Add numbers
          </button>
        </div>
      </div>
      {job && (
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="font-medium">
              {job.status === "complete"
                ? `Release complete — ${job.released} disconnected${job.failed ? `, ${job.failed} failed (will retry)` : ""}`
                : job.status === "failed"
                ? `Release stopped — ${job.released} disconnected, ${job.failed} not (will retry)`
                : `Releasing ${job.quarantined_only ? "quarantined " : ""}numbers at the carrier…`}
            </span>
            <span className="text-gray-500 tabular-nums">
              {job.done}/{job.total}
            </span>
          </div>
          <div className="h-2 rounded bg-gray-200 overflow-hidden">
            <div
              className={`h-full transition-all ${job.status === "failed" ? "bg-red-500" : job.status === "complete" ? "bg-green-500" : "bg-brand"}`}
              style={{ width: `${job.total ? Math.round((job.done / job.total) * 100) : 0}%` }}
            />
          </div>
          {jobDone && (
            <button
              onClick={() => { setJobId(null); }}
              className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Dismiss
            </button>
          )}
        </div>
      )}
      {isLoading ? (
        <p className="text-gray-500 text-sm p-4">Loading…</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Number</th>
              <th className="px-4 py-2">Label</th>
              <th className="px-4 py-2">Reputation</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {members.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-3 text-gray-500">
                  No numbers in this group yet.
                </td>
              </tr>
            ) : (
              members.map((d) => (
                <tr key={d.did_id}>
                  <td className="px-4 py-2 font-mono">{d.phone}</td>
                  <td className="px-4 py-2">{d.name || "—"}</td>
                  <td className="px-4 py-2">{statusBadge(d)}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => remove.mutate(d.did_id)}
                      className="text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
      {showAdd && (
        <AddMembersModal
          groupId={group.did_group_id}
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

export function CallerIdGroupsPage() {
  const qc = useQueryClient();
  const { data: groups, isLoading, isError, error } = useQuery({
    queryKey: ["did-groups"],
    queryFn: listDidGroups,
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [newName, setNewName] = useState("");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["did-groups"] });
  const create = useMutation({
    mutationFn: (name: string) => createDidGroup(name),
    onSuccess: () => {
      setNewName("");
      invalidate();
    },
  });
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => updateDidGroup(id, name),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: number) => deleteDidGroup(id),
    onSuccess: () => {
      setSelectedId(null);
      invalidate();
    },
  });

  const selected = (groups ?? []).find((g) => g.did_group_id === selectedId) ?? null;

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-2xl font-bold">Caller ID Groups</h1>
        <p className="text-sm text-gray-500">
          Pools of phone numbers rotated as the outbound caller ID for campaigns. Numbers flagged by
          carrier reputation are automatically skipped while dialing.
        </p>
      </div>

      {isError ? (
        <p className="text-red-600">{(error as Error).message}</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Groups list */}
          <div className="bg-white rounded-lg shadow overflow-hidden lg:col-span-1 h-fit">
            <div className="px-4 py-2 border-b border-gray-100 flex gap-2">
              <input
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                placeholder="New group name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newName.trim()) create.mutate(newName.trim());
                }}
              />
              <button
                disabled={!newName.trim() || create.isPending}
                onClick={() => create.mutate(newName.trim())}
                className="bg-brand hover:bg-brand-dark text-white rounded px-3 text-sm disabled:opacity-50"
              >
                Add
              </button>
            </div>
            {isLoading ? (
              <p className="text-gray-500 text-sm p-4">Loading…</p>
            ) : (groups ?? []).length === 0 ? (
              <p className="text-gray-500 text-sm p-4">No groups yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {(groups ?? []).map((g) => (
                  <li
                    key={g.did_group_id}
                    className={`px-4 py-2 flex items-center justify-between cursor-pointer ${
                      selectedId === g.did_group_id ? "bg-brand/5" : "hover:bg-gray-50"
                    }`}
                    onClick={() => setSelectedId(g.did_group_id)}
                  >
                    <span className="text-sm">
                      {g.name}{" "}
                      <span className="text-gray-400">({g.member_count})</span>
                    </span>
                    <span className="space-x-2 text-xs">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const name = prompt("Rename group", g.name);
                          if (name && name.trim()) rename.mutate({ id: g.did_group_id, name: name.trim() });
                        }}
                        className="text-brand hover:underline"
                      >
                        Rename
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (g.member_count > 0) {
                            alert("Remove all numbers from the group before deleting it.");
                            return;
                          }
                          if (confirm(`Delete group "${g.name}"?`)) remove.mutate(g.did_group_id);
                        }}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Selected group detail */}
          <div className="lg:col-span-2">
            {selected ? (
              <GroupDetail group={selected} />
            ) : (
              <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400 text-sm">
                Select a group to manage its numbers.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
